import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { GoogleTagManager } from '@iwoca/frontend-tracking-library';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './DropdownMenu.module.css';
import { getIwocaPayDashboardLink } from './Navigation';
import { NavLink } from './NavLink';
import { ProductDashboardLinks } from './ProductDashboardLinks';
import { isIwocaPaySeller } from '../../api/iwocaPaySelector';
import IwMedia from '../../iwMedia/IwMedia';
import { OPEN_MODAL } from '../../iwModals/Modals.actions';
import { RootState } from '../../iwProvider/configureStore';

const mapStateToProps = (state: RootState) => ({
    isIwocaPaySeller: isIwocaPaySeller(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    openHelpModal: () =>
        dispatch({
            type: OPEN_MODAL,
            id: 'HelpModal'
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const DropdownMenu = ({
    isOpen,
    setIsOpen,
    isIwocaPaySeller,
    openHelpModal
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
} & PropsFromRedux) => {
    const { data } = useGetContext();

    const isAccessUser = data?.logged_in_user_type === 'access_user';

    return (
        <div
            data-testid="dropdown-navigation"
            className={classNames(
                styles.DropdownNavigation,
                isOpen ? styles.navigationOpen : styles.navigationClosed
            )}
        >
            <div className={styles.navigationContainer}>
                <IwMedia to="small">
                    <div className={styles.navItemSection}>
                        {isIwocaPaySeller && (
                            <a
                                data-testid="iwocapay-link"
                                className={styles.dropdownNavLink}
                                href={getIwocaPayDashboardLink()}
                            >
                                <FormattedMessage
                                    id="_.navigation.iwocapayDashboard"
                                    defaultMessage="iwocaPay Seller"
                                />
                            </a>
                        )}
                        <button
                            className={classNames(
                                styles.dropdownNavLink,
                                styles.dropdownNavButton
                            )}
                            onClick={() => {
                                setIsOpen(false);
                                openHelpModal();
                            }}
                        >
                            <FormattedMessage
                                id="_.navigation.help"
                                defaultMessage="Help"
                            />
                        </button>
                    </div>
                </IwMedia>

                {!isAccessUser && (
                    <IwMedia to="medium">
                        <div className={styles.navItemSection}>
                            <span className={styles.navItemSectionHeader}>
                                <FormattedMessage
                                    id="_.navigation.loanDashboard"
                                    defaultMessage="Loan Dashboard"
                                />
                            </span>

                            <div
                                className="flex flex-col [&>*]:w-full [&>*]:text-left [&>*]:![border-bottom:1px_solid_var(--color-structure-95)]"
                                data-testid="product-links-mobile"
                            >
                                <ProductDashboardLinks />
                            </div>
                        </div>
                    </IwMedia>
                )}

                <div className="flex flex-col items-start justify-start">
                    <span className="w-full border-b border-structure-95 pb-m text-xs uppercase text-structure-60">
                        <FormattedMessage
                            id="_.navigation.account"
                            defaultMessage="Account"
                        />
                    </span>

                    {isAccessUser ? (
                        <>
                            <span className="w-full border-b border-structure-95">
                                <NavLink
                                    data-testid="napg-view-link"
                                    to="/napg-view/"
                                >
                                    <FormattedMessage
                                        id="_.navigation.napg-view"
                                        defaultMessage="Home"
                                    />
                                </NavLink>
                            </span>
                            <span className="w-full border-b border-structure-95">
                                <NavLink
                                    data-testid="history-link"
                                    to="/history/"
                                >
                                    <FormattedMessage
                                        id="_.navigation.personal_guarantees"
                                        defaultMessage="Personal Guarantees"
                                    />
                                </NavLink>
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="w-full border-b border-structure-95">
                                <NavLink
                                    data-testid="company-link"
                                    to="/company/"
                                >
                                    <FormattedMessage
                                        id="_.navigation.your_company"
                                        defaultMessage="Profile"
                                    />
                                </NavLink>
                            </span>

                            <span className="w-full border-b border-structure-95">
                                <NavLink
                                    data-testid="history-link"
                                    to="/history/"
                                >
                                    <FormattedMessage
                                        id="_.navigation.account_history"
                                        defaultMessage="Transactions"
                                    />
                                </NavLink>
                            </span>

                            <span className="w-full border-b border-structure-95">
                                <NavLink
                                    data-testid="trading-data-link"
                                    to="/trading/"
                                >
                                    <FormattedMessage
                                        id="_.navigation.trading_data"
                                        defaultMessage="Upload Documents"
                                    />
                                </NavLink>
                            </span>
                        </>
                    )}

                    <span className="w-full border-b border-structure-95">
                        <NavLink
                            data-testid="contracts-data-link"
                            to="/contracts/"
                        >
                            <FormattedMessage
                                id="_.navigation.contracts"
                                defaultMessage="Contracts"
                            />
                        </NavLink>
                    </span>

                    {getCountry() === 'DE' && (
                        <span className="w-full border-b border-structure-95">
                            <NavLink
                                data-testid="referral-link"
                                to="/referral/"
                            >
                                <FormattedMessage
                                    id="_.navigation.referral"
                                    defaultMessage="Kunden-werben-Freunde"
                                />
                            </NavLink>
                        </span>
                    )}

                    <a
                        href={
                            getCountry() === 'UK'
                                ? 'https://www.iwoca.co.uk/compliance/terms-of-use#1-general'
                                : 'https://www.iwoca.de/compliance/allgemeine-geschaeftsbedingungen'
                        }
                        className={styles.dropdownNavLink}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <FormattedMessage
                            id="_.navigation.terms_of_use"
                            defaultMessage="Terms of use"
                        />
                    </a>

                    <a
                        href="/my_account/logout/"
                        className={classNames(
                            styles.dropdownNavLink,
                            styles.logout
                        )}
                        onClick={() =>
                            GoogleTagManager.trackClick({
                                identifier: 'Logout',
                                deviceType: 'Desktop'
                            })
                        }
                    >
                        <FormattedMessage
                            id="_.navigation.logout"
                            defaultMessage="Logout"
                        />
                    </a>
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(DropdownMenu);
